import { Building2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { isPlatformAdmin, useAuthStore } from '@/features/auth';
import { useSetTenantScope, useTenantScope } from '../hooks/useTenantScope';
import { useTenants } from '../hooks/useTenants';
import {
	tenantScopedScreen,
	type TenantScopedScreen,
} from '../lib/tenantScopedScreens';

/**
 * Header tenant picker: platform admins, on the screens that can show another
 * tenant (`TENANT_SCOPED_SCREENS`). The pick is kept across screens and
 * reloads.
 */
export function HeaderTenantPicker() {
	const role = useAuthStore((state) => state.user?.role);
	const screen = tenantScopedScreen(useLocation().pathname);

	return isPlatformAdmin(role) && screen ? <Picker screen={screen} /> : null;
}

function Picker({ screen }: { screen: TenantScopedScreen }) {
	const ownTenantId = useAuthStore((state) => state.user?.tenantId);
	const scope = useTenantScope();
	const setScope = useSetTenantScope();
	const tenants = useTenants();

	return (
		<div className="flex min-w-0 items-center gap-2">
			<Select
				value={scope.tenantId ?? ownTenantId ?? ''}
				onValueChange={setScope}
				disabled={tenants.isPending || tenants.isError}
			>
				<SelectTrigger
					size="sm"
					className="w-36 sm:w-56"
					aria-label="Tenant"
					title={tenants.isError ? 'Could not load tenants' : 'Tenant shown'}
				>
					<Building2 className="text-muted-foreground" aria-hidden="true" />
					<SelectValue
						placeholder={
							tenants.isPending
								? 'Loading tenants…'
								: tenants.isError
									? 'Tenants unavailable'
									: 'Your tenant'
						}
					/>
				</SelectTrigger>
				<SelectContent>
					{(tenants.data ?? []).map((tenant) => (
						<SelectItem key={tenant.id} value={tenant.id}>
							<span className="truncate">{tenant.name}</span>
							{tenant.id === ownTenantId ? (
								<span className="text-muted-foreground text-xs">Yours</span>
							) : tenant.status === 'suspended' ? (
								<span className="text-destructive text-xs">Suspended</span>
							) : null}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			{screen.readOnly && scope.readOnly && (
				<Badge variant="secondary" className="hidden sm:inline-flex">
					Read-only
				</Badge>
			)}
		</div>
	);
}
